import { cn } from "@/lib/utils";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'text' | 'card' | 'button' | 'circle';
  width?: string | number;
  height?: string | number;
}

function Skeleton({
  className,
  variant = 'default',
  width,
  height,
  style,
  ...props
}: SkeletonProps) {
  const variantStyles = {
    default: "h-4 w-full",
    text: "h-4",
    card: "h-24 w-full",
    button: "h-10 w-24",
    circle: "h-12 w-12 rounded-full"
  };

  const customStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style
  };

  return (
    <div
      className={cn(
        "skeleton",
        variantStyles[variant],
        className
      )}
      style={customStyle}
      {...props}
    />
  );
}

function SkeletonCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("surface-elevated p-3", className)} {...props}>
      <div className="flex flex-col items-center space-y-2">
        <Skeleton variant="circle" className="w-5 h-5" />
        <Skeleton variant="text" className="w-12" />
        <Skeleton variant="text" className="w-16" />
      </div>
    </div>
  );
}

function SkeletonTableRow({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex items-center gap-4 p-3 border rounded-lg", className)} {...props}>
      <Skeleton variant="default" className="h-4 w-4" />
      <Skeleton variant="default" className="h-12 w-12" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="default" className="h-4 w-48" />
        <Skeleton variant="default" className="h-3 w-32" />
      </div>
      <Skeleton variant="default" className="h-4 w-20" />
      <Skeleton variant="default" className="h-4 w-16" />
    </div>
  );
}

function SkeletonText({ 
  className, 
  lines = 1, 
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & { lines?: number }) {
  return (
    <div className={cn("space-y-2", className)} {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          variant="text" 
          className={cn(
            "h-4",
            i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"
          )} 
        />
      ))}
    </div>
  );
}

function SkeletonChart({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("surface-elevated p-6", className)} {...props}>
      <div className="space-y-4">
        <Skeleton variant="text" className="w-32" />
        <div className="h-48 bg-muted/50 rounded-md flex items-end justify-between p-4 space-x-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="bg-muted rounded-sm animate-pulse flex-1"
              style={{ height: `${Math.random() * 80 + 20}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export { 
  Skeleton, 
  SkeletonCard, 
  SkeletonTableRow, 
  SkeletonText, 
  SkeletonChart 
};